import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { auth } from '../../../firebase-core';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { signInWithGoogleAuto, checkEmailExists } from '../../../shared/api/firebase';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { Mail, ChevronLeft } from 'lucide-react';

const RegisterModal = ({ isOpen, onClose, onSuccess, onRequestLogin }) => {
  const { currentUser } = useAuth();

  const [mode, setMode] = useState('select'); // 'select' | 'email'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    agreePrivacy: false,
  });

  useEffect(() => {
    if (isOpen && currentUser) {
      onSuccess?.(currentUser);
      onClose?.();
    }
  }, [isOpen, currentUser, onSuccess, onClose]);

  useEffect(() => {
    if (isOpen) {
      setMode('select');
      setIsLoading(false);
      setError('');
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        agreeTerms: false,
        agreePrivacy: false,
      });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleGoogleSignup = async () => {
    setError('');
    try {
      const result = await signInWithGoogleAuto(true);
      if (result?.error) {
        if (result.error === 'popup-closed-by-user') return;
        setError(result.error);
      }
    } catch (err) {
      const code = err?.code || '';
      const message = err?.message || '';
      if (code === 'auth/popup-closed-by-user' || message.includes('popup-closed-by-user')) return;
      setError('Google 가입 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (!formData.agreeTerms || !formData.agreePrivacy) {
      setError('필수 약관에 동의해주세요.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const existsCheck = await checkEmailExists(formData.email);
      if (existsCheck.exists) {
        setError('이미 가입된 이메일입니다. 로그인으로 진행해주세요.');
        setIsLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      if (formData.name.trim()) {
        await updateProfile(user, { displayName: formData.name.trim() });
      }
      // 이후 동의/데이터 저장은 상위(MainHeader)에서 처리
    } catch (err) {
      if (err?.code === 'auth/weak-password') {
        setError('비밀번호가 너무 약합니다. 6자리 이상 입력해주세요.');
      } else if (err?.code === 'auth/invalid-email') {
        setError('잘못된 이메일 형식입니다.');
      } else {
        setError(err?.message || '회원가입에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const headerTitle = mode === 'email' ? '이메일로 가입하기' : '회원가입';
  const header = (
    <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
      <p className="modal-title">{headerTitle}</p>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={undefined} showCloseButton>
      {mode === 'email' && (
        <div className="modal-header">
            <button
                type="button"
                className="modal-back-btn"
                aria-label="뒤로"
                onClick={() => { setError(''); setMode('select'); }}
                style={{ right: 'auto', left: 'var(--spacing-md)' }}
            >
                <ChevronLeft />
            </button>
      </div>
      )}

      {header}

      {mode === 'select' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
          <button
            type="button"
            className="btn-simple large"
            style={{ width: '100%' }}
            onClick={() => setMode('email')}
          >
            <Mail size={18} />
            이메일로 가입하기
          </button>

          <button
            type="button"
            onClick={handleGoogleSignup}
            className="btn-ghost large"
            style={{ width: '100%' }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 18,
                height: 18,
                display: 'inline-block',
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='18' height='18' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z' fill='%234285F4'/%3E%3Cpath d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z' fill='%2334A853'/%3E%3Cpath d='M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z' fill='%23FBBC05'/%3E%3Cpath d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z' fill='%23EA4335'/%3E%3C/g%3E%3C/svg%3E\")",
                backgroundSize: '18px 18px',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                marginRight: 8
              }}
            />
            Google로 가입하기
          </button>

          <div style={{ textAlign: 'center', paddingTop: 'var(--spacing-md)', borderTop: '1px solid var(--gray-200)' }}>
            <p style={{ fontSize: 'var(--font-base)', color: 'var(--gray-600)', margin: 0 }}>
              이미 계정이 있으신가요? <a href="/login" className="btn-link" onClick={(e) => { e.preventDefault(); onRequestLogin?.(); }}>로그인</a>
            </p>
          </div>
        </div>
      )}

      {mode === 'email' && (
        <form onSubmit={handleEmailSignup} className="auth-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">이메일</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input"
              placeholder="이메일을 입력하세요"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name" className="form-label">닉네임</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              placeholder="닉네임을 입력하세요"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">비밀번호</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="form-input"
              placeholder="비밀번호를 입력하세요"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">비밀번호 확인</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="form-input"
              placeholder="비밀번호를 다시 입력하세요"
              required
            />
          </div>

          <div className="form-options" style={{ flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            <label className="form-checkbox-container">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleInputChange}
                className="form-checkbox"
                required
              />
              <span className="form-checkbox-text">
                <span className="form-required">*</span> <a href="/help-center/terms-of-service" className="btn-link" target="_blank" rel="noopener noreferrer">이용약관</a>에 동의합니다
              </span>
            </label>

            <label className="form-checkbox-container">
              <input
                type="checkbox"
                name="agreePrivacy"
                checked={formData.agreePrivacy}
                onChange={handleInputChange}
                className="form-checkbox"
                required
              />
              <span className="form-checkbox-text">
                <span className="form-required">*</span> <a href="/help-center/privacy-policy" className="btn-link" target="_blank" rel="noopener noreferrer">개인정보 처리방침</a>에 동의합니다
              </span>
            </label>
          </div>

          <button type="submit" className="btn-primary large" disabled={isLoading}>
            {isLoading ? '회원가입 중...' : '회원가입 완료'}
          </button>
        </form>
      )}

      {error && <div className="form-message-error">{error}</div>}
    </Modal>
  );
};

export default RegisterModal;


